import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { ShoppingBag, Heart, Minus, Plus, Truck, Snowflake, ShieldCheck, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSettings } from "@/context/SettingsContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user, setUser } = useAuth();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${slug}`);
      setP(data);
      setQty(1);
      setActiveImg(0);
    })().catch(() => navigate("/urunler"));
  }, [slug, navigate]);

  if (!p) return <div className="section-pad container-max">Yükleniyor...</div>;

  const isFav = user?.favorites?.includes(p.id);
  const discount = p.old_price > p.price ? Math.round(((p.old_price - p.price) / p.old_price) * 100) : 0;

  const handleFav = async () => {
    if (!user) { toast.error("Lütfen önce giriş yapın"); return; }
    const { data } = await api.post(`/me/favorites/${p.id}`);
    setUser({ ...user, favorites: data.favorites });
    toast.success(isFav ? "Favorilerden çıkarıldı" : "Favorilere eklendi");
  };

  const waMsg = encodeURIComponent(`Merhaba, "${p.name}" ürününden ${qty} adet sipariş etmek istiyorum.`);

  return (
    <div data-testid="product-detail-page" className="bg-white">
      <div className="container-max px-4 md:px-8 py-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="aspect-square rounded-2xl overflow-hidden bg-[#F4F7F9]">
            <img src={p.images[activeImg]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[#0077B6]" : "border-transparent"}`}>
                  <img src={img} className="w-full h-full object-cover" alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="overline mb-3">Premium Tazelik</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mb-3">{p.name}</h1>

          <div className="flex items-end gap-3 mb-2">
            <span className="font-display text-4xl font-black text-[#0077B6]" data-testid="detail-price">{formatTL(p.price)}</span>
            {p.old_price > p.price && <span className="text-slate-400 line-through pb-1">{formatTL(p.old_price)}</span>}
            {discount > 0 && <span className="bg-[#E9C46A] text-[#0A192F] text-xs font-extrabold px-2.5 py-1 rounded-full">%{discount}</span>}
          </div>
          {p.stock > 0 ? (
            <div className="text-emerald-700 font-semibold text-sm mb-5">● {p.stock} adet stokta · Bugün kargoda</div>
          ) : (
            <div className="text-rose-600 font-semibold text-sm mb-5">● Stok Tükendi</div>
          )}

          <p className="text-slate-600 leading-relaxed mb-7">{p.description}</p>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center border-2 border-slate-200 rounded-full">
              <button data-testid="qty-decrement" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 hover:bg-slate-50 rounded-l-full"><Minus size={16} /></button>
              <span data-testid="qty-value" className="w-12 text-center font-bold">{qty}</span>
              <button data-testid="qty-increment" onClick={() => setQty((q) => q + 1)} className="p-2.5 hover:bg-slate-50 rounded-r-full"><Plus size={16} /></button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={() => addItem(p, qty)}
              className="flex-1 btn-primary"
              disabled={p.stock < 1}
            >
              <ShoppingBag size={16} /> Sepete Ekle
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              data-testid="buy-now"
              onClick={() => { addItem(p, qty); navigate("/sepet"); }}
              className="btn-secondary"
            >
              Hemen Satın Al
            </button>
            <a
              data-testid="whatsapp-order"
              href={`https://wa.me/${settings.contact_whatsapp}?text=${waMsg}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-3 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              <MessageCircle size={16} /> WhatsApp Sipariş
            </a>
          </div>

          <button
            data-testid="favorite-toggle"
            onClick={handleFav}
            className={`flex items-center gap-2 text-sm font-semibold ${isFav ? "text-rose-600" : "text-slate-600"}`}
          >
            <Heart size={16} className={isFav ? "fill-rose-600" : ""} /> {isFav ? "Favorilerimde" : "Favorilere Ekle"}
          </button>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              [Snowflake, "Soğuk Zincir"],
              [Truck, "Hızlı Kargo"],
              [ShieldCheck, "Güvenli Ödeme"],
            ].map(([Icon, label]) => (
              <div key={label} className="bg-[#F4F7F9] rounded-xl p-4 text-center">
                <Icon size={20} className="mx-auto text-[#0077B6] mb-1.5" />
                <div className="text-xs font-semibold">{label}</div>
              </div>
            ))}
          </div>

          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="ship">
              <AccordionTrigger data-testid="ship-info">Otobüs ile Teslimat Bilgileri</AccordionTrigger>
              <AccordionContent>Aynı gün hasat, soğuk zincir paketleme. Türkiye'nin tüm otogarlarına otobüs ile gönderim. Sabit 400 TL otobüs ücreti. Müşterimiz otogardan teslim alır.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="store">
              <AccordionTrigger>Saklama Koşulları</AccordionTrigger>
              <AccordionContent>Buzdolabında +2 / +4°C aralığında 3 gün, dondurucuda 1 ay saklanabilir.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {p.similar?.length > 0 && (
        <section className="container-max px-4 md:px-8 py-16">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mb-8">Benzer Ürünler</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.similar.map((sp) => <ProductCard key={sp.id} product={sp} />)}
          </div>
        </section>
      )}
    </div>
  );
}
